// shared/stores/useNotificationsStore.ts
import { create } from 'zustand';
import { UserInfoNotification } from '../../types/notifications';
import { API_BASE_URL } from '../../constants/config';
import { getAndSanitize } from '../utils/sanitization';

interface NotificationsState {
  notifications: UserInfoNotification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;

  // Actions
  fetchNotifications: (adminId: string, status?: 'PENDING' | 'RESOLVED') => Promise<void>;
  addNotification: (notification: UserInfoNotification) => void;
  markAsResolved: (notificationId: string, adminId: string) => Promise<void>;
  updateNotification: (notificationId: string, updates: Partial<UserInfoNotification>) => void;
  clearError: () => void;
}

export const useNotificationsStore = create<NotificationsState>((set) => ({
  notifications: [],
  unreadCount: 0,
  loading: false,
  error: null,

  fetchNotifications: async (adminId: string, status?: 'PENDING' | 'RESOLVED') => {
    set({ loading: true, error: null });

    try {
      const token = getAndSanitize('token');
      if (!token) {
        throw new Error('No authentication token found');
      }


      // Construir URL con query params
      const params = new URLSearchParams();
      params.append('adminId', adminId);
      if (status) {
        params.append('status', status);
      }

      const authHeader = `Bearer ${token}`;

      const response = await fetch(
        `${API_BASE_URL}/api/panel/user-info-requests?${params.toString()}`,
        {
          headers: {
            'Authorization': authHeader,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        const notifications = data.data || [];
        const unread = notifications.filter((n: UserInfoNotification) => n.status === 'PENDING').length;

        set({
          notifications,
          unreadCount: unread,
          loading: false
        });
      } else {
        throw new Error(data.message || 'Failed to fetch notifications');
      }
    } catch (error) {
      console.error('[NotificationsStore] Error fetching notifications:', error);
      set({
        error: error instanceof Error ? error.message : 'Unknown error',
        loading: false
      });
    }
  },

  addNotification: (notification: UserInfoNotification) => {
    set((state) => {
      // Verificar si ya existe para evitar duplicados
      const exists = state.notifications.some(n => n.id === notification.id);
      if (exists) {
        return state;
      }

      return {
        notifications: [notification, ...state.notifications],
        unreadCount: notification.status === 'PENDING' ? state.unreadCount + 1 : state.unreadCount
      };
    });
  },

  markAsResolved: async (notificationId: string, adminId: string) => {
    set({ loading: true, error: null });

    try {
      const token = getAndSanitize('token');
      if (!token) {
        throw new Error('No authentication token found');
      }


      const response = await fetch(
        `${API_BASE_URL}/api/panel/user-info/${notificationId}/resolve?adminId=${adminId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'adminid': adminId
          }
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[NotificationsStore] HTTP error:', response.status, errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();

      if (data.success) {
        // Actualizar la notificación en el store
        set((state) => ({
          notifications: state.notifications.map(n =>
            n.id === notificationId
              ? { ...n, status: 'RESOLVED' as const, resolvedAt: new Date().toISOString() }
              : n
          ),
          unreadCount: Math.max(0, state.unreadCount - 1),
          loading: false
        }));
      } else {
        throw new Error(data.message || 'Failed to resolve notification');
      }
    } catch (error) {
      console.error('[NotificationsStore] Error resolving notification:', error);
      set({
        error: error instanceof Error ? error.message : 'Unknown error',
        loading: false
      });
      throw error; // Re-throw para que el componente pueda manejarlo
    }
  },

  updateNotification: (notificationId: string, updates: Partial<UserInfoNotification>) => {
    set((state) => ({
      notifications: state.notifications.map(n =>
        n.id === notificationId ? { ...n, ...updates } : n
      ),
      unreadCount: updates.status === 'RESOLVED' && state.notifications.find(n => n.id === notificationId)?.status === 'PENDING'
        ? Math.max(0, state.unreadCount - 1)
        : state.unreadCount
    }));
  },

  clearError: () => set({ error: null })
}));
